// Hero.tsx (main component)
import React, {memo, useEffect} from "react";
import {motion} from "framer-motion";
import {FaBolt, FaLock, FaMicrophone, FaPlayCircle, FaTv, FaWifi,} from "react-icons/fa";

// Add TypeScript interface for feature items
interface FeatureItemProps {
    icon: React.ReactNode;
    text: string;
}

// Enhanced features with more compelling messaging
const FEATURES: FeatureItemProps[] = [
    {
        icon: <FaTv className="text-blue-600 dark:text-blue-400 w-5 h-5"/>,
        text: "Control any smart TV without extra hardware",
    },
    {
        icon: <FaWifi className="text-blue-600 dark:text-blue-400 w-5 h-5"/>,
        text: "Instant Wi-Fi connection to Roku, Samsung, LG & more",
    },
    {
        icon: <FaBolt className="text-blue-600 dark:text-blue-400 w-5 h-5"/>,
        text: "Lightning-fast response with zero lag",
    },
    {
        icon: <FaPlayCircle className="text-blue-600 dark:text-blue-400 w-5 h-5"/>,
        text: "One-tap access to Netflix, Disney+ and more",
    },
    {
        icon: <FaMicrophone className="text-blue-600 dark:text-blue-400 w-5 h-5"/>,
        text: "Voice search across all your streaming services",
    },
    {
        icon: <FaLock className="text-blue-600 dark:text-blue-400 w-5 h-5"/>,
        text: "Secure, private connection with no data collection",
    },
];

// Animation variants
const containerVariants = {
    hidden: {opacity: 0},
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
        },
    },
};

const Hero = () => {
    useEffect(() => {
        // Set theme color for mobile browsers once on mount
        if (!document.querySelector('meta[name="theme-color"]')) {
            const metaThemeColor = document.createElement("meta");
            metaThemeColor.name = "theme-color";
            metaThemeColor.content = "#1E40AF"; // blue-800
            document.head.appendChild(metaThemeColor);
        }
    }, []);

    return (
        <div className="min-h-screen flex flex-col relative overflow-hidden">
            {/* Background and decorative elements */}
            <div
                className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-950 dark:via-indigo-950 dark:to-purple-950"/>
            <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>

            {/* Floating decorative elements */}
            <FloatingElements/>

            {/* Header with logo and download button */}
            <Header/>

            {/* Main content */}
            <main className="flex-grow px-6 pt-8 pb-16 md:pt-12 md:pb-20 relative z-10">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="max-w-7xl mx-auto"
                >
                    <div className="flex flex-col md:flex-row items-center justify-between gap-12 lg:gap-20">
                        {/* Left column - content */}
                        <ContentSection features={FEATURES}/>

                        {/* Right column - device mockup */}
                        <DeviceMockup/>
                    </div>
                </motion.div>
            </main>

            {/* TV Brands Support */}
            <SupportedBrands/>

            {/* Footer */}
            <Footer/>
        </div>
    );
};

// Component for the floating decorative elements
const FloatingElements = memo(() => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
            className="absolute w-64 h-64 rounded-full bg-blue-400/10 dark:bg-blue-600/10"
            style={{top: "15%", left: "5%"}}
            animate={{y: [0, 15, 0], x: [0, 10, 0]}}
            transition={{repeat: Infinity, duration: 8, ease: "easeInOut"}}
        />
        <motion.div
            className="absolute w-32 h-32 rounded-full bg-purple-400/10 dark:bg-purple-600/10"
            style={{top: "60%", right: "10%"}}
            animate={{y: [0, -20, 0], x: [0, -5, 0]}}
            transition={{repeat: Infinity, duration: 10, ease: "easeInOut"}}
        />
        <motion.div
            className="absolute w-48 h-48 rounded-full bg-indigo-400/10 dark:bg-indigo-600/10"
            style={{bottom: "20%", left: "15%"}}
            animate={{y: [0, 10, 0], x: [0, 5, 0]}}
            transition={{repeat: Infinity, duration: 9, ease: "easeInOut"}}
        />
    </div>
));

// Header component
const Header = memo(() => (
    <header className="relative z-10 pt-6 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-2">
                <img src="/logo192.png" alt="Logo" width={48} height={48}/>
                <span className="font-bold text-xl text-gray-900 dark:text-white">
          Smart TV Remote
        </span>
            </div>

            <AppStoreButton
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-5 rounded-xl transition-all shadow-md hover:shadow-lg text-sm md:text-base flex items-center gap-2"/>
        </div>
    </header>
));

// App Store Button component
const AppStoreButton = memo(({className}: { className?: string }) => (
    <a
        href="https://apps.apple.com/app/id1631533361"
        target="_blank"
        rel="noopener noreferrer"
        className={className}
    >
        <svg className="w-5 h-5" viewBox="0 0 384 512" fill="currentColor">
            <path
                d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
        </svg>
        Download Now
    </a>
));

// Feature item component
const FeatureItemComponent = memo(
    ({
         icon,
         text,
         index,
     }: {
        icon: React.ReactNode;
        text: string;
        index: number;
    }) => (
        <motion.div
            key={index}
            className="flex items-center gap-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-3 rounded-lg shadow-sm hover:shadow-md transition-shadow"
            initial={{opacity: 0, x: -20}}
            animate={{opacity: 1, x: 0}}
            transition={{delay: 0.3 + index * 0.1}}
        >
            <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg shrink-0">
                {icon}
            </div>
            <span className="text-gray-800 dark:text-gray-200 font-medium text-sm">
        {text}
      </span>
        </motion.div>
    ),
);

// Star rating component
const StarRating = memo(() => (
    <div className="flex">
        {Array(5)
            .fill(0)
            .map((_, i) => (
                <svg
                    key={i}
                    className="text-yellow-400 w-6 h-6"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                >
                    <path
                        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
            ))}
    </div>
));

// Main content section
const ContentSection = memo(
    ({features}: { features: FeatureItemProps[] }) => (
        <motion.div
            initial={{opacity: 0, x: -20}}
            animate={{opacity: 1, x: 0}}
            transition={{duration: 0.8, ease: "easeOut"}}
            className="max-w-xl w-full"
        >
            <motion.h1
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.8, delay: 0.3}}
                className="font-bold text-4xl md:text-5xl lg:text-6xl mb-6 text-gray-900 dark:text-white leading-tight"
            >
        <span
            className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">
          Your Phone,
        </span>
                <br/>
                The Ultimate Remote
            </motion.h1>

            <p className="text-xl text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                Control any smart TV with your iPhone. Instantly connects to Roku,
                Samsung, LG, and more without IR blasters or extra hardware. Just tap
                and watch.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {features.map((feature, index) => (
                    <FeatureItemComponent
                        key={index}
                        icon={feature.icon}
                        text={feature.text}
                        index={index}
                    />
                ))}
            </div>

            <div className="flex justify-start mb-8">
                <motion.a
                    href="https://apps.apple.com/app/id1631533361"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative group"
                    whileHover={{scale: 1.05}}
                    initial={{scale: 1}}
                >
                    <img
                        src="/appstore.svg"
                        alt="Download on the App Store"
                        className="h-[60px] md:h-[70px] shadow-md rounded-lg transition-all group-hover:shadow-lg"
                        draggable="false"
                    />
                    <div
                        className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white text-xs py-1 px-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        Free Download
                    </div>
                </motion.a>
            </div>

            <motion.div
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{delay: 0.3, duration: 0.6}}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-5 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700"
            >
                <div className="flex items-center gap-4">
                    <StarRating/>
                    <div className="flex flex-col">
            <span className="text-gray-800 dark:text-white font-semibold">
              4.9 out of 5
            </span>
                        <span className="text-gray-600 dark:text-gray-400 text-sm">
              Based on 50,000+ user reviews
            </span>
                    </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-gray-700 dark:text-gray-300 italic text-sm">
                        "Finally, an app that actually works with all my TVs without any
                        hassle!"
                    </p>
                </div>
            </motion.div>
        </motion.div>
    ),
);

// Device mockup section
const DeviceMockup = memo(() => (
    <motion.div
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        transition={{delay: 0.6, duration: 0.8}}
        className="w-full max-w-sm md:max-w-md relative"
    >
        <div className="relative w-full overflow-hidden">
            <motion.div
                className="relative"
                initial={{scale: 1}}
                animate={{scale: [1, 1.02, 1]}}
                transition={{repeat: Infinity, duration: 3}}
            >
                <div
                    className="absolute -top-10 -right-10 bg-blue-600 text-white text-sm font-bold py-1 px-4 rounded-full rotate-12 shadow-lg z-10">
                    No IR Blaster Needed!
                </div>
                <img
                    src="/promo.png"
                    alt="TV Remote App Interface"
                    className="w-full h-auto object-contain relative z-0 rounded-3xl shadow-2xl"
                    draggable="false"
                />
            </motion.div>
        </div>
    </motion.div>
));

// Supported TV brands section
const SupportedBrands = memo(() => (
    <section
        className="py-12 bg-gray-50/90 dark:bg-gray-900/90 backdrop-blur-sm relative z-10 border-t border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-3 text-gray-900 dark:text-white">
                Compatible With All Major TV Brands
            </h2>
            <p className="text-center text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
                Our app supports all modern smart TVs and streaming devices. If it's
                connected to Wi-Fi, we can control it.
            </p>
            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-10">
                {[
                    {name: "Roku", color: "blue"},
                    {name: "Samsung", color: "indigo"},
                    {name: "LG", color: "purple"},
                    {name: "Sony", color: "pink"},
                    {name: "Android TV", color: "green"},
                    {name: "Apple TV", color: "gray"},
                    {name: "Vizio", color: "yellow"},
                    {name: "TCL", color: "red"},
                ].map((brand) => (
                    <motion.div
                        key={brand.name}
                        className="text-center"
                        initial={{opacity: 0, y: 20}}
                        animate={{opacity: 1, y: 0}}
                        transition={{delay: Math.random() * 0.5}}
                    >
                        <div
                            className={`bg-white dark:bg-gray-800 p-4 md:p-5 rounded-2xl shadow-md`}
                        >
                            <p className="font-semibold text-gray-800 dark:text-gray-200">
                                {brand.name}
                            </p>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
));

// Footer component
const Footer = memo(() => (
    <footer
        className="py-8 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-t border-gray-200 dark:border-gray-800 relative z-10">
        <div className="max-w-7xl mx-auto px-6">

            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-500">
                <a
                    href="/privacy-policy"
                    className="hover:text-blue-500 transition-colors"
                >
                    Privacy Policy
                </a>
                <span className="hidden md:inline">•</span>
                <a
                    href="/terms-of-service"
                    className="hover:text-blue-500 transition-colors"
                >
                    Terms of Service
                </a>
                <span className="hidden md:inline">•</span>
                <a href="/help" className="hover:text-blue-500 transition-colors">
                    Help Center
                </a>
                <span className="hidden md:inline">•</span>
                <a href="/contact" className="hover:text-blue-500 transition-colors">
                    Contact Us
                </a>
            </div>
        </div>
    </footer>
));

export default memo(Hero);
